import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { AudioProvider } from './context/AudioContext';
import { StudioLineProvider } from './context/StudioLineContext';
import { Navbar } from './components/Navbar';
import { HomeHeroPlayer } from './components/HomeHeroPlayer';
import { MobileBottomNav } from './components/MobileBottomNav';
import { IncomingCallDock } from './components/IncomingCallDock';
import { StudioCallScreen } from './components/StudioCallScreen';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useAudio } from './context/AudioContext';
import { Heart, AlertCircle, X, Megaphone } from 'lucide-react';
import { RadioCard } from './components/RadioCard';
import { App as CapApp } from '@capacitor/app';
import { detectTv, backAction, TV_TABS } from './utils/tv';
import { installSpatialNav } from './utils/spatialNav';

// تحميل عند الطلب.
//
// سابقاً كانت كل الشاشات والنوافذ — بما فيها المجلس ولوحة الأدمن — تُحمَّل
// في حزمة واحدة مع أول فتح رغم أن أغلب المستخدمين لا يفتحونها إطلاقاً.
const RadioGrid = lazy(() => import('./components/RadioGrid').then((m) => ({ default: m.RadioGrid })));
const PrayerTimesSection = lazy(() => import('./components/PrayerTimesSection').then((m) => ({ default: m.PrayerTimesSection })));
const IslamicLessonsSection = lazy(() => import('./components/IslamicLessonsSection').then((m) => ({ default: m.IslamicLessonsSection })));
const FatwaSection = lazy(() => import('./components/FatwaSection').then((m) => ({ default: m.FatwaSection })));
const StudioContactSection = lazy(() => import('./components/StudioContactSection').then((m) => ({ default: m.StudioContactSection })));
const TasbeehAndAthkarModal = lazy(() => import('./components/TasbeehAndAthkarModal').then((m) => ({ default: m.TasbeehAndAthkarModal })));
const MushafModal = lazy(() => import('./components/MushafModal').then((m) => ({ default: m.MushafModal })));
const DailyVerseCardModal = lazy(() => import('./components/DailyVerseCardModal').then((m) => ({ default: m.DailyVerseCardModal })));
const MajlisAlTayyibeenModal = lazy(() => import('./components/MajlisAlTayyibeenModal').then((m) => ({ default: m.MajlisAlTayyibeenModal })));
const FullscreenPlayer = lazy(() => import('./components/FullscreenPlayer').then((m) => ({ default: m.FullscreenPlayer })));
const SleepTimerModal = lazy(() => import('./components/SleepTimerModal').then((m) => ({ default: m.SleepTimerModal })));
const AddStationModal = lazy(() => import('./components/AddStationModal').then((m) => ({ default: m.AddStationModal })));
const AdminModal = lazy(() => import('./components/AdminModal').then((m) => ({ default: m.AdminModal })));
const SidebarDrawer = lazy(() => import('./components/SidebarDrawer').then((m) => ({ default: m.SidebarDrawer })));

/** مؤشر تحميل بسيط أثناء جلب الشاشة المطلوبة. */
const SectionFallback = () => (
  <div className="flex items-center justify-center py-16" role="status" aria-label="جاري التحميل">
    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

function MobileAppLayout() {
  const {
    visibleStations = [],
    favorites = [],
    addStation,
    updateStation,
    activeTheme,
    errorMsg,
    clearError,
    siteSettings
  } = useAudio();

  // Active Mobile View Tab: 'home' | 'radios' | 'prayer' | 'lessons' | 'favorites' | 'fatwa' | 'studio'
  const [activeMobileTab, setActiveMobileTab] = useState('home');

  // Modals & Drawers
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingStation, setEditingStation] = useState(null);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [isSleepTimerOpen, setIsSleepTimerOpen] = useState(false);
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
  const [isTasbeehModalOpen, setIsTasbeehModalOpen] = useState(false);
  const [isDailyVerseModalOpen, setIsDailyVerseModalOpen] = useState(false);
  const [isMajlisModalOpen, setIsMajlisModalOpen] = useState(false);
  const [isMushafOpen, setIsMushafOpen] = useState(false);

  // التلفزيون: الصنف tv على <html> يبدّل الإطار (المتغيّر tv: في Tailwind)،
  // والأسهم تنقل التركيز بين الأزرار
  const [isTv, setIsTv] = useState(false);
  useEffect(() => {
    let cancelled = false;
    detectTv().then((tv) => {
      if (cancelled) return;
      setIsTv(tv);
      document.documentElement.classList.toggle('tv', tv);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => (isTv ? installSpatialNav(document) : undefined), [isTv]);

  // زر الرجوع: يغلق النافذة، ثم يرجع للرئيسية، ثم يخرج — في الجوال والتلفزيون
  const modalOpen =
    isSidebarOpen || isAddModalOpen || isFullscreenOpen || isSleepTimerOpen || isAdminModalOpen ||
    isTasbeehModalOpen || isDailyVerseModalOpen || isMajlisModalOpen || isMushafOpen;
  const backStateRef = useRef({ modalOpen, tab: activeMobileTab });
  backStateRef.current = { modalOpen, tab: activeMobileTab };
  useEffect(() => {
    if (!window.Capacitor?.isNativePlatform?.()) return undefined;
    const closeAll = () => {
      [setIsSidebarOpen, setIsAddModalOpen, setIsFullscreenOpen, setIsSleepTimerOpen, setIsAdminModalOpen,
        setIsTasbeehModalOpen, setIsDailyVerseModalOpen, setIsMajlisModalOpen, setIsMushafOpen].forEach((set) => set(false));
    };
    const handle = CapApp.addListener('backButton', () => {
      const action = backAction(backStateRef.current);
      if (action === 'close-modal') closeAll();
      else if (action === 'go-home') setActiveMobileTab('home');
      else CapApp.exitApp();
    });
    return () => {
      handle.then((h) => h.remove());
    };
  }, []);

  // على التلفزيون قسمٌ يحتاج ميكروفوناً أو كتابة لا يُفتح
  useEffect(() => {
    if (isTv && !TV_TABS.includes(activeMobileTab)) setActiveMobileTab('home');
  }, [isTv, activeMobileTab]);

  const handleOpenAddModal = () => {
    setEditingStation(null);
    setIsAddModalOpen(true);
  };

  const handleEditStation = (station) => {
    setEditingStation(station);
    setIsAddModalOpen(true);
  };

  const handleSaveStation = (stationData) => {
    if (editingStation) {
      updateStation(editingStation.id, stationData);
    } else {
      addStation(stationData);
    }
  };

  return (
    <div className="min-h-screen bg-paper text-ink flex justify-center">
      {/* عمود واحد بعرض الجوال. لا هالات مضبّبة ولا نقش خلفي: كانت
          تكلفة رسم عالية على أندرويد المتوسط مقابل أثر بصري لا يكاد يُرى
          على خلفية فاتحة. */}
      <div className="w-full max-w-md tv:max-w-none tv:pr-44 tv:pb-6 min-h-screen bg-paper flex flex-col relative sm:border-x sm:border-line tv:border-x-0 pb-28">

        {/* Mobile Header Navbar */}
        <Navbar
          onOpenAddModal={handleOpenAddModal}
          onToggleSidebar={() => setIsSidebarOpen(true)}
          onOpenTasbeehModal={() => setIsTasbeehModalOpen(true)}
          onOpenDailyVerseModal={() => setIsDailyVerseModalOpen(true)}
        />

        {/* شريط الإعلان — كان announcementText/showAnnouncement قابلين للتعديل
            في لوحة الأدمن لكنهما لا يُقرآن في أي مكان من التطبيق. */}
        {siteSettings?.showAnnouncement && siteSettings?.announcementText && (
          <div className="px-4 py-2.5 rule bg-primary/[0.05]">
            <p className="text-xs text-ink/80 leading-relaxed flex items-center gap-2">
              <Megaphone className="w-3.5 h-3.5 flex-shrink-0 text-primary" aria-hidden="true" />
              <span className="truncate">{siteSettings.announcementText}</span>
            </p>
          </div>
        )}

        {/* تنبيه أخطاء البث على مستوى التطبيق.
            سابقاً كانت رسالة الخطأ تُعرض داخل AudioPlayer وحده، وهو مخفي في
            تبويب الرئيسية — فكان فشل التشغيل صامتاً تماماً في الشاشة الأهم. */}
        {errorMsg && (
          <div
            role="alert"
            className="mx-4 mt-2 px-3 py-2.5 rounded-md bg-live/10 border border-live/25 text-live text-xs flex items-start gap-2 animate-slideDown"
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <span className="flex-1 leading-relaxed">{errorMsg}</span>
            <button
              onClick={clearError}
              aria-label="إغلاق التنبيه"
              className="flex-shrink-0 opacity-70 hover:opacity-100"
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        )}

        {/* Dynamic Mobile View Content based on active tab */}
        <main className="flex-1 p-3.5 space-y-4">
          <Suspense fallback={<SectionFallback />}>
          
          {/* TAB 1: HOME DASHBOARD (الرئيسية) */}
          {(activeMobileTab === 'home' || activeMobileTab === 'stations') && (
            <div className="space-y-4 animate-fadeIn">
              <HomeHeroPlayer
                onNavigateTab={setActiveMobileTab}
                onOpenMajlisModal={() => setIsMajlisModalOpen(true)}
                onOpenMushaf={() => setIsMushafOpen(true)}
                onOpenFullscreen={() => setIsFullscreenOpen(true)}
              />
            </div>
          )}

          {/* TAB 2: RADIOS (الإذاعات) */}
          {activeMobileTab === 'radios' && (
            <RadioGrid
              onOpenAddModal={handleOpenAddModal}
              onEditStation={handleEditStation}
            />
          )}

          {/* TAB 3: PRAYER TIMES SECTION (مواقيت الصلاة الدقيقة) */}
          {activeMobileTab === 'prayer' && (
            <PrayerTimesSection />
          )}

          {/* TAB 4: ISLAMIC LESSONS SECTION (الدروس والمحاضرات العلمية) */}
          {activeMobileTab === 'lessons' && (
            <IslamicLessonsSection />
          )}

          {/* FATWA SECTION (متاح من الروابط السريعة) */}
          {activeMobileTab === 'fatwa' && (
            <FatwaSection />
          )}

          {/* STUDIO CONTACT SECTION (متاح من الروابط السريعة) */}
          {activeMobileTab === 'studio' && (
            <StudioContactSection />
          )}

          {/* TAB 4: FAVORITES (المفضلة) */}
          {activeMobileTab === 'favorites' && (
            <div className="space-y-4 animate-fadeIn">
              <div className="flex items-center gap-2 pb-3">
                <Heart className="w-4 h-4 text-live fill-current" aria-hidden="true" />
                <h3 className="text-sm font-semibold text-ink">
                  المحطات والإذاعات المفضلة ({favorites.length})
                </h3>
              </div>

              {favorites.length > 0 ? (
                <div className="-mx-4">
                  {visibleStations
                    .filter(s => favorites.includes(s.id))
                    .map((station) => (
                      <RadioCard
                        key={station.id}
                        station={station}
                        onEdit={handleEditStation}
                      />
                    ))}
                </div>
              ) : (
                <div className="py-14 text-center space-y-3">
                  <Heart className="w-7 h-7 text-ink-muted/40 mx-auto" aria-hidden="true" />
                  <p className="text-xs text-ink-muted max-w-[16rem] mx-auto leading-relaxed">
                    اضغط القلب على أي إذاعة لتجدها هنا.
                  </p>
                </div>
              )}
            </div>
          )}

          </Suspense>
        </main>

        {/* Mobile Bottom Navigation Bar */}
        <MobileBottomNav
          activeMobileTab={activeMobileTab}
          setActiveMobileTab={setActiveMobileTab}
          onOpenAdminModal={() => setIsAdminModalOpen(true)}
        />

        <Suspense fallback={null}>
        {/* Sidebar Drawer with Login Form */}
        <SidebarDrawer
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          activeMobileTab={activeMobileTab}
          setActiveMobileTab={setActiveMobileTab}
          onOpenSleepTimer={() => setIsSleepTimerOpen(true)}
          onOpenMushaf={() => setIsMushafOpen(true)}
          onOpenAdminModal={() => setIsAdminModalOpen(true)}
          onOpenAddModal={handleOpenAddModal}
        />

        {/* مكالمة الاستديو تأخذ الشاشة كاملة، والواردة تطفو للمسؤول */}
        <StudioCallScreen />
        <IncomingCallDock />

        {/* Modals */}
        <AdminModal
          isOpen={isAdminModalOpen}
          onClose={() => setIsAdminModalOpen(false)}
        />

        <AddStationModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSave={handleSaveStation}
          editingStation={editingStation}
        />

        <SleepTimerModal
          isOpen={isSleepTimerOpen}
          onClose={() => setIsSleepTimerOpen(false)}
        />

        <FullscreenPlayer
          isOpen={isFullscreenOpen}
          onClose={() => setIsFullscreenOpen(false)}
          onOpenSleepTimer={() => setIsSleepTimerOpen(true)}
        />

        <TasbeehAndAthkarModal
          isOpen={isTasbeehModalOpen}
          onClose={() => setIsTasbeehModalOpen(false)}
        />

        <DailyVerseCardModal
          isOpen={isDailyVerseModalOpen}
          onClose={() => setIsDailyVerseModalOpen(false)}
        />

        <MajlisAlTayyibeenModal
          isOpen={isMajlisModalOpen}
          onClose={() => setIsMajlisModalOpen(false)}
        />

        {/* يُحمَّل عند أول فتح: المصحف ومكوّنه لا يدخلان حزمة الإقلاع */}
        {isMushafOpen && <MushafModal isOpen onClose={() => setIsMushafOpen(false)} />}

        </Suspense>

      </div>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AudioProvider>
        <StudioLineProvider>
          <MobileAppLayout />
        </StudioLineProvider>
      </AudioProvider>
    </ErrorBoundary>
  );
}
